// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Request/response helper over the BFF WebSocket: send a message stamped with
// a requestId and resolve on the first matching ack frame (or reject on
// timeout). Frames that carry a different requestId are never matched; frames
// without one (older backends) pass unless a custom `match` rejects them.

import { createEventId } from './offlineQueue';

export type AckSocket = {
  send(payload: Record<string, unknown>): unknown;
  onMessage(handler: (message: unknown) => void): () => void;
};

export type SendAndAwaitAckOptions = {
  timeoutMs?: number;
  /** Use a caller-provided requestId instead of generating one. */
  requestId?: string;
  /** Extra predicate a candidate ack frame must pass (after type/requestId checks). */
  match?: (frame: Record<string, unknown>) => boolean;
};

export const sendAndAwaitAck = (
  ws: AckSocket,
  message: Record<string, unknown>,
  ackTypes: string[],
  options?: SendAndAwaitAckOptions
): Promise<Record<string, unknown>> =>
  new Promise((resolve, reject) => {
    const requestId = options?.requestId ?? createEventId();
    const timeoutMs = options?.timeoutMs ?? 12000;
    let settled = false;
    const unsubscribe = ws.onMessage((incoming: unknown) => {
      if (settled || !incoming || typeof incoming !== 'object') return;
      const frame = incoming as Record<string, unknown>;
      const type = typeof frame.type === 'string' ? frame.type : '';
      if (!ackTypes.includes(type)) return;
      const frameRequestId = typeof frame.requestId === 'string' ? frame.requestId : null;
      if (frameRequestId && frameRequestId !== requestId) return;
      if (options?.match && !options.match(frame)) return;
      settled = true;
      unsubscribe();
      clearTimeout(timer);
      resolve(frame);
    });
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      reject(new Error(`Timed out waiting for ${ackTypes[0]}.`));
    }, timeoutMs);
    ws.send({ ...message, requestId });
  });
